/**
 * Wejście partnera na konto klienta — sześciocyfrowa zgoda.
 * ========================================================
 *
 * Zaplecze tego mechanizmu (migracja 459) działa od dawna: najemca generuje u
 * siebie w sekcji Pomoc sześciocyfrowy kod, partner go wykupuje i dostaje
 * CZASOWE członkostwo w organizacji klienta, a cała sesja jest nagrywana.
 * Problem był po stronie ekranu: formularz istniał w JEDNYM miejscu — w rogu
 * „Analityki klientów" — a ta strona przy braku kontekstu partnera kończyła
 * się komunikatem o braku profilu, więc pole na kod nigdy się nie renderowało.
 * Partner miał funkcję, o której nie wiedział, że ją ma.
 *
 * Dlatego to jest komponent, a nie fragment jednej strony: ten sam formularz
 * stoi na pulpicie, na liście klientów i w analityce.
 */

import { KeyRound, Loader2, X as XIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useWejscieWsparcia } from '../../hooks/useSupportAccess';
import { useAuth } from '../../providers/AuthProvider';

export interface WejscieNaKontoKlientaProps {
  /**
   * `pelny` — kafelek z opisem; `kompaktowy` — sam pasek z polem;
   * `topbar` — przycisk w górnym pasku rozwijający pole (dostępny wszędzie).
   */
  wariant?: 'pelny' | 'kompaktowy' | 'topbar';
  /**
   * Organizacja partnera, w którego imieniu wchodzimy (`partnerOrgId`
   * z `usePartnerContext`). Baza rozstrzyga uprawnienie sama, ale bez tej
   * wartości nie wie, kogo dotyczy zgoda, gdy panel ogląda super-admin
   * w trybie podglądu.
   */
  partnerOrgId?: string | null;
  className?: string;
}

export function WejscieNaKontoKlienta({
  wariant = 'pelny',
  partnerOrgId = null,
  className,
}: WejscieNaKontoKlientaProps) {
  const [kod, setKod] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const { wchodzi, wejdz } = useWejscieWsparcia();
  const { impersonatingPartner, stopImpersonation } = useAuth();

  // Wariant `topbar` jest zwinięty do przycisku: pasek u góry towarzyszy każdej
  // stronie panelu, więc pole na kod stoi tam na stałe — ale rozłożone
  // zabierałoby miejsce okruszkom i wyszukiwarce na węższych ekranach.
  const [otwarty, setOtwarty] = useState(false);
  const kotwica = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!otwarty) return;
    const pozaPanelem = (e: MouseEvent) => {
      if (!kotwica.current?.contains(e.target as Node)) setOtwarty(false);
    };
    const naEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOtwarty(false);
    };
    document.addEventListener('mousedown', pozaPanelem);
    document.addEventListener('keydown', naEscape);
    return () => {
      document.removeEventListener('mousedown', pozaPanelem);
      document.removeEventListener('keydown', naEscape);
    };
  }, [otwarty]);

  const wyslij = async (e: React.FormEvent) => {
    e.preventDefault();
    setBlad(null);
    try {
      await wejdz(kod, partnerOrgId);
      // Podgląd panelu partnera i wejście na konto najemcy to dwa różne
      // konteksty i nie mogą trwać naraz: efekt przywracający podgląd
      // w `AuthProvider` na siłę ustawia organizację partnera, więc bez tego
      // wejście lądowałoby z powrotem w panelu partnera.
      if (impersonatingPartner) stopImpersonation();
      // Po wykupieniu kodu partner ma czasowe członkostwo w organizacji klienta
      // — pełne przeładowanie jest tu celowe, bo kontekst organizacji i
      // uprawnienia czyta cała powłoka przy starcie. Organizacja NIE jedzie w
      // adresie: `AuthProvider` odczytuje ją z sesji wsparcia zapisanej przez
      // `wejdz()` w `sessionStorage`.
      window.location.href = '/admin';
    } catch (err) {
      setBlad(err instanceof Error ? err.message : 'Nie udało się wejść na konto klienta');
    }
  };

  const pole = (
    <>
      <div className="flex items-center gap-2">
        <input
          value={kod}
          onChange={(e) => setKod(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          aria-label="Kod dostępu od klienta"
          className="w-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded-ids-md text-aurora-text text-lg font-mono tracking-widest text-center focus:outline-none focus:border-amber-500"
        />
        <button
          type="submit"
          disabled={wchodzi || kod.length !== 6}
          className="px-4 py-2 bg-gradient-to-r from-amber-500 to-green-600 text-aurora-on-accent text-sm font-medium rounded-ids-md hover:from-amber-600 hover:to-green-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {wchodzi && <Loader2 className="w-4 h-4 animate-spin" />}
          Wejdź na konto
        </button>
      </div>

      {blad && <p className="text-xs text-red-400 mt-2">{blad}</p>}

      {wariant !== 'kompaktowy' && (
        <p className="text-[11px] text-gray-500 mt-2 leading-relaxed max-w-sm">
          Klient generuje kod u siebie w sekcji <strong className="text-gray-400">Pomoc</strong>.
          Kod jest jednorazowy i wygasa po 30 minutach. Cała sesja jest nagrywana i widoczna dla
          klienta — bez jego zgody wejście nie jest możliwe.
        </p>
      )}
    </>
  );

  // ── Wariant paska górnego: przycisk + rozwijany panel ─────────────────────
  if (wariant === 'topbar') {
    return (
      <div className={`relative ${className ?? ''}`} ref={kotwica}>
        <button
          type="button"
          onClick={() => setOtwarty((o) => !o)}
          className="iconbtn hover:text-amber-400 hover:bg-amber-500/15"
          title="Wejście na konto klienta (kod od klienta)"
          aria-label="Wejście na konto klienta"
          aria-expanded={otwarty}
        >
          <KeyRound className="w-5 h-5" />
        </button>

        {otwarty && (
          <form
            onSubmit={wyslij}
            className="absolute right-0 top-full mt-2 w-[min(22rem,calc(100vw-2rem))] z-50 bg-aurora-surface border border-aurora-border rounded-ids-lg shadow-elev-4 p-4"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-xs text-aurora-muted flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                Wejście na konto klienta — kod od klienta (6 cyfr)
              </p>
              <button
                type="button"
                onClick={() => setOtwarty(false)}
                className="iconbtn -mt-1 -mr-1"
                aria-label="Zamknij"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            {pole}
          </form>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={wyslij}
      className={`bg-gray-800/50 border border-gray-700 rounded-ids-lg p-4 ${className ?? ''}`}
    >
      <p className="text-xs text-gray-400 mb-2 flex items-center gap-1.5">
        <KeyRound className="w-3.5 h-3.5 text-amber-400" />
        Wejście na konto klienta — kod od klienta (6 cyfr)
      </p>
      {pole}
    </form>
  );
}

export default WejscieNaKontoKlienta;
